import { ASPECT_RATIOS, type AspectPresetId, type Size } from '../geometry/framing.ts';
import { DEFAULT_CAPTION_LINES, type CaptionLines } from '../captions/placement.ts';
import type { CaptionSettings } from '../captions/types.ts';
import { MAX_VERTICAL_ASPECT, PLATFORM_OVERLAYS, type ShortsPlatform } from './safe-area.ts';
import { SIZE_LIMIT_RANGE, sizeInMb, type StatedSize } from './size-target.ts';
import type { EditRecipe, FormatSettings } from './recipe.ts';

/**
 * Where a video is going to be posted, and what that place asks of it
 * (docs/seo-plan.md SC-05): the shape, the length and the largest file.
 * `?fit=<id>` opens the app set up for one of these; the spec pages show
 * the same numbers.
 *
 * Every number here is copied from the platform's own page: the page, the
 * sentence and the day it was read are in content/pseo/data/placements.ts,
 * and build/pseo-specs.test.ts fails when a number has no source there.
 * A fact the platform doesn't publish stays out (rule 3: no invented numbers).
 */

export const PLACEMENT_IDS = ['instagram-reels', 'instagram-story', 'tiktok', 'youtube-shorts', 'youtube', 'linkedin', 'x', 'pinterest', 'shopify'] as const;
export type PlacementId = (typeof PLACEMENT_IDS)[number];

/** A picture size, width × height in pixels, and what the platform says it is. */
export interface StatedResolution extends Size {
  readonly kind: 'recommended' | 'minimum' | 'maximum';
}

/** Share of the frame (0–1) the platform asks to keep free of words and logos, per edge. */
export interface SafeZone {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

/** Who may post longer than `maxLengthSec`, and up to how long. */
export interface LongerFor {
  readonly sec: number;
  readonly who: 'verified' | 'premium';
}

export interface Placement {
  readonly id: PlacementId;
  /** The platform's name as it writes it. */
  readonly platform: string;
  /**
   * The frame Shycut makes for it: one of the app's shapes, or `original`
   * when the platform takes the shapes cameras make (see `accepts`).
   */
  readonly frame: AspectPresetId;
  /** The narrowest and widest shapes it takes (width ÷ height), when it says. */
  readonly accepts?: { readonly min: number; readonly max: number };
  readonly resolution?: StatedResolution;
  readonly minLengthSec?: number;
  readonly maxLengthSec?: number;
  readonly longerFor?: LongerFor;
  readonly maxSize?: StatedSize;
  readonly safeZone?: SafeZone;
  /** Whose buttons the editor's guide draws over a vertical video (an approximate guide, src/core/project/safe-area.ts). */
  readonly overlay?: ShortsPlatform;
}

export const PLACEMENTS: Readonly<Record<PlacementId, Placement>> = {
  'instagram-reels': {
    id: 'instagram-reels',
    platform: 'Instagram',
    frame: '9:16',
    resolution: { kind: 'recommended', width: 1440, height: 2560 },
    maxLengthSec: 15 * 60,
    maxSize: { value: 4, unit: 'GB' },
    safeZone: { top: 0.14, bottom: 0.35, left: 0.06, right: 0.06 },
    overlay: 'reels',
  },
  'instagram-story': {
    id: 'instagram-story',
    platform: 'Instagram',
    frame: '9:16',
    resolution: { kind: 'recommended', width: 1440, height: 2560 },
    minLengthSec: 1,
    maxLengthSec: 60 * 60,
    maxSize: { value: 4, unit: 'GB' },
    safeZone: { top: 0.14, bottom: 0.35, left: 0.06, right: 0.06 },
  },
  tiktok: {
    id: 'tiktok',
    platform: 'TikTok',
    frame: '9:16',
    resolution: { kind: 'minimum', width: 540, height: 960 },
    maxLengthSec: 10 * 60,
    maxSize: { value: 500, unit: 'MB' },
    overlay: 'tiktok',
  },
  'youtube-shorts': {
    id: 'youtube-shorts',
    platform: 'YouTube',
    frame: '9:16',
    // "Square or vertical": anything up to as wide as it is tall.
    accepts: { min: 0, max: 1 },
    maxLengthSec: 3 * 60,
    maxSize: { value: 256, unit: 'GB' },
    overlay: 'shorts',
  },
  youtube: {
    id: 'youtube',
    platform: 'YouTube',
    frame: '16:9',
    maxLengthSec: 15 * 60,
    longerFor: { sec: 12 * 60 * 60, who: 'verified' },
    maxSize: { value: 256, unit: 'GB' },
  },
  linkedin: {
    id: 'linkedin',
    platform: 'LinkedIn',
    frame: 'original',
    accepts: { min: 1 / 2.4, max: 2.4 },
    resolution: { kind: 'maximum', width: 4096, height: 2304 },
    minLengthSec: 3,
    maxLengthSec: 15 * 60,
    maxSize: { value: 5, unit: 'GB' },
  },
  x: {
    id: 'x',
    platform: 'X',
    frame: 'original',
    accepts: { min: 1 / 3, max: 3 },
    resolution: { kind: 'recommended', width: 1280, height: 720 },
    minLengthSec: 0.5,
    maxLengthSec: 20 * 60,
    longerFor: { sec: 125 * 60, who: 'premium' },
    maxSize: { value: 8, unit: 'GB' },
  },
  pinterest: {
    id: 'pinterest',
    platform: 'Pinterest',
    // Pinterest recommends square or vertical (2:3, 4:5 or 9:16); 4:5 is the closest Shycut makes to its 2:3 Pin.
    frame: '4:5',
    accepts: { min: 1 / 2, max: 1.91 },
    minLengthSec: 4,
    maxLengthSec: 15 * 60,
    maxSize: { value: 2, unit: 'GB' },
  },
  shopify: {
    id: 'shopify',
    platform: 'Shopify',
    frame: 'original',
    resolution: { kind: 'maximum', width: 4096, height: 2160 },
    maxLengthSec: 10 * 60,
    maxSize: { value: 1, unit: 'GB' },
  },
};

/** A placement by id; null for anything else (`constructor`, `__proto__`, typos). */
export const placementById = (id: string | null | undefined): Placement | null => (id && Object.hasOwn(PLACEMENTS, id) ? PLACEMENTS[id as PlacementId] : null);

/** The id as saved in a project, or null when it isn't one (yet, or any more). */
export const parsePlacement = (value: unknown): PlacementId | null => (typeof value === 'string' ? (placementById(value)?.id ?? null) : null);

const takes = (placement: Placement, aspect: number): boolean => !placement.accepts || (aspect >= placement.accepts.min && aspect <= placement.accepts.max);

/**
 * The shape to make for a video: the placement's own, or for a placement that
 * takes the shapes cameras make, the video's shape when it is taken and else
 * the closest one Shycut makes that is.
 */
export function placementFrame(placement: Placement, source: Size): AspectPresetId {
  if (placement.frame !== 'original') return placement.frame;
  const aspect = source.width / source.height;
  if (takes(placement, aspect)) return 'original';
  const candidates = (Object.entries(ASPECT_RATIOS) as [Exclude<AspectPresetId, 'original'>, number][]).filter(([, ratio]) => takes(placement, ratio));
  // Shapes compare by how many times wider or narrower they are, so 1:2 and 2:1 are as far from 1:1.
  const distance = (ratio: number) => Math.abs(Math.log(ratio / aspect));
  return candidates.sort(([, a], [, b]) => distance(a) - distance(b))[0]?.[0] ?? 'original';
}

/**
 * Whether the platform takes a frame of this shape (width ÷ height): within
 * the shapes it states, or for one that names a single shape, that shape.
 */
export function takesShape(placement: Placement, aspect: number): boolean {
  if (placement.accepts) return takes(placement, aspect);
  return placement.frame === 'original' || Math.abs(aspect - ASPECT_RATIOS[placement.frame]) < 0.01;
}

/**
 * How the picture goes into the frame, the way the built-in recipes do it: a
 * tall frame is cut out of the video, following the main face; a wide frame
 * is built around a tall video on a blurred copy of itself, so nobody's head
 * is cut off. Full HD when the shape changes, the video's own size when not.
 */
export function placementFormat(placement: Placement, source: Size): Omit<FormatSettings, 'focus'> {
  const aspect = placementFrame(placement, source);
  if (aspect === 'original') return { aspect, fit: 'crop', resolution: 'source', followSubject: false };
  const wide = ASPECT_RATIOS[aspect] > 1;
  return { aspect, fit: wide ? 'blurFill' : 'crop', resolution: '1080', followSubject: !wide };
}

/**
 * An edit set up for a placement: its frame, its largest file as the size
 * limit, and the placement remembered so the editor can check the length as
 * it changes. Everything else the edit already has stays as it is.
 */
export function applyPlacement(recipe: EditRecipe, placement: Placement, source: Size): EditRecipe {
  return {
    ...recipe,
    format: { ...placementFormat(placement, source), focus: { x: 0.5, y: 0.5 } },
    sizeLimit: placementSizeLimit(placement) ?? recipe.sizeLimit,
    placement: placement.id,
  };
}

/** The placement's largest file as the export's "keep it under" limit, or null when it has none the field can hold. */
export function placementSizeLimit(placement: Placement): number | null {
  if (!placement.maxSize) return null;
  const mb = sizeInMb(placement.maxSize);
  return mb >= SIZE_LIMIT_RANGE.min && mb <= SIZE_LIMIT_RANGE.max ? mb : null;
}

/** How an edit's length sits with a placement. */
export type LengthCheck =
  | { readonly kind: 'fits' }
  | { readonly kind: 'short'; readonly minSec: number }
  | { readonly kind: 'long'; readonly maxSec: number }
  /** Longer than most accounts may post, but within what `who` may. */
  | { readonly kind: 'longer-for'; readonly maxSec: number; readonly who: LongerFor['who'] };

/** Lengths are compared to the tenth of a second the export keeps. */
const EPSILON = 0.05;

export function checkLength(placement: Placement, durationSec: number): LengthCheck {
  const { minLengthSec, maxLengthSec, longerFor } = placement;
  if (minLengthSec !== undefined && durationSec < minLengthSec - EPSILON) return { kind: 'short', minSec: minLengthSec };
  if (maxLengthSec === undefined || durationSec <= maxLengthSec + EPSILON) return { kind: 'fits' };
  if (longerFor && durationSec <= longerFor.sec + EPSILON) return { kind: 'longer-for', maxSec: maxLengthSec, who: longerFor.who };
  return { kind: 'long', maxSec: longerFor?.sec ?? maxLengthSec };
}

/**
 * Where captions go on a video fitted for a place: below what it asks to keep
 * clear at the top and above it at the bottom — Meta's stated safe zone for
 * Reels and Stories, the editor's guide to the app's own buttons otherwise.
 * Only on a vertical frame (that's where the apps draw over the picture), and
 * never lower or higher than the usual lines. Undefined when nothing moves.
 */
export function captionLines(placement: Placement | null, output: Size): CaptionLines | undefined {
  if (!placement || !(output.width > 0 && output.height > 0) || output.width / output.height > MAX_VERTICAL_ASPECT) return undefined;
  const clear = placement.safeZone ?? (placement.overlay ? PLATFORM_OVERLAYS[placement.overlay] : null);
  if (!clear) return undefined;
  const lines = { top: Math.max(DEFAULT_CAPTION_LINES.top, clear.top), bottom: Math.min(DEFAULT_CAPTION_LINES.bottom, 1 - clear.bottom) };
  return lines.top === DEFAULT_CAPTION_LINES.top && lines.bottom === DEFAULT_CAPTION_LINES.bottom ? undefined : lines;
}

/** The caption settings a render gets: the recipe's own, on the fitted place's lines (see `captionLines`). */
export function placedCaptions(recipe: Pick<EditRecipe, 'captions' | 'placement'>, output: Size): CaptionSettings {
  const lines = captionLines(placementById(recipe.placement), output);
  return lines ? { ...recipe.captions, lines } : recipe.captions;
}

